import type {
  CellStatus,
  MonthlyCell,
  MonthlyRow,
  MonthlyRowSummary,
} from "@/lib/types";
import { getMonthlyDaySessions } from "@/lib/monthly";

/**
 * Presence % based on days that count toward attendance.
 * O (official leave) and TP (taken permission) are excluded from the denominator.
 * H and pending are also excluded (not accountable).
 * S (sick) counts like A — in the total, not present.
 */
export function computePresencePct(statuses: CellStatus[]): {
  present: number;
  late: number;
  absent: number;
  on_leave: number;
  sick: number;
  taken_permission: number;
  holiday: number;
  pending: number;
  accountable_days: number;
  present_pct: number | null;
  /** When every eligible day is O and/or TP (and there is at least one). */
  all_out: boolean;
} {
  let present = 0;
  let late = 0;
  let absent = 0;
  let on_leave = 0;
  let sick = 0;
  let taken_permission = 0;
  let holiday = 0;
  let pending = 0;

  for (const status of statuses) {
    switch (status) {
      case "P":
        present += 1;
        break;
      case "L":
        late += 1;
        break;
      case "A":
        absent += 1;
        break;
      case "O":
        on_leave += 1;
        break;
      case "S":
        sick += 1;
        break;
      case "TP":
        taken_permission += 1;
        break;
      case "H":
        holiday += 1;
        break;
      case "pending":
        pending += 1;
        break;
    }
  }

  const accountable_days = present + late + absent + sick;
  const present_pct =
    accountable_days > 0
      ? Math.round(((present + late) / accountable_days) * 10000) / 100
      : null;
  const all_out =
    accountable_days === 0 && (on_leave > 0 || taken_permission > 0);

  return {
    present,
    late,
    absent,
    on_leave,
    sick,
    taken_permission,
    holiday,
    pending,
    accountable_days,
    present_pct,
    all_out,
  };
}

/** One status per day for % (first session if multi-shift). */
export function dayStatusFromMonthlyCell(
  cell: MonthlyCell | undefined
): CellStatus | null {
  if (!cell) return null;
  const sessions = getMonthlyDaySessions(cell);
  if (sessions.length === 0) {
    return cell.status === "pending" ? "pending" : cell.status;
  }
  return sessions[0].status;
}

export function summarizeMonthlyRow(
  row: Pick<MonthlyRow, "cells">,
  dates: string[]
): MonthlyRowSummary & { all_out: boolean } {
  const statuses: CellStatus[] = [];
  for (const d of dates) {
    const st = dayStatusFromMonthlyCell(row.cells[d]);
    if (st) statuses.push(st);
  }
  const s = computePresencePct(statuses);
  return {
    present: s.present,
    late: s.late,
    absent: s.absent,
    on_leave: s.on_leave,
    sick: s.sick,
    taken_permission: s.taken_permission,
    holiday: s.holiday,
    pending: s.pending,
    accountable_days: s.accountable_days,
    present_pct: s.present_pct,
    absent_pct:
      s.accountable_days > 0
        ? Math.round((s.absent / s.accountable_days) * 10000) / 100
        : null,
    all_out: s.all_out,
  };
}

export function formatPresencePct(
  pct: number | null,
  allOut: boolean
): string {
  if (allOut) return "—";
  if (pct == null) return "—";
  return `${pct.toFixed(2)}%`;
}
